<template>
  <b-container fluid class="event-notification-rule-page py-3">
    <div class="iq-card">
      <div class="iq-card-header d-flex align-items-center justify-content-between">
        <div class="iq-header-title py-2">
          <h4 class="card-title mb-0">Event Notification Rule</h4>
        </div>
        <div class="header-actions">
          <b-button variant="outline-secondary" size="sm" :to="{ name: 'MasterConfiguration' }">
            Back
          </b-button>
          <b-button variant="primary" size="sm" :disabled="isLoading" @click="fetchList">
            <i v-if="isLoading && activeMethod === 'GET'" class="ri-loader-line mr-1"></i>
            Refresh
          </b-button>
        </div>
      </div>

      <div class="iq-card-body">
        <b-alert v-if="message.text" show :variant="message.variant" class="mb-3">
          {{ message.text }}
        </b-alert>

        <b-row>
          <b-col lg="12" class="mb-3">
            <section class="panel">
              <div class="table-toolbar">
                <div>
                  <h5 class="panel-title mb-1">Rules</h5>
                  <small class="text-muted">{{ totalData }} data</small>
                </div>
                <div class="table-actions">
                  <b-button variant="success" size="sm" :disabled="isLoading" @click="openAddRuleModal">
                    Add Rule
                  </b-button>
                  <div class="pagination-control">
                    <b-form-select
                      v-model="pagination.itemPerPage"
                      :options="pageSizeOptions"
                      size="sm"
                      :disabled="isLoading"
                      @change="onPageSizeChanged"
                    />
                  </div>
                </div>
              </div>

              <div class="filter-panel">
                <b-row>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Event" class="mb-0">
                      <searchable-select
                        v-model="filters.event"
                        :fetcher="searchEvents"
                        placeholder="All events"
                        :disabled="isLoading"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Asset" class="mb-0">
                      <searchable-select
                        v-model="filters.asset"
                        :fetcher="searchAssets"
                        placeholder="All assets"
                        :disabled="isLoading"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Recipient" class="mb-0">
                      <searchable-select
                        v-model="filters.recipient"
                        :fetcher="searchRecipients"
                        placeholder="All recipients"
                        :disabled="isLoading"
                      />
                    </b-form-group>
                  </b-col>

                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Type" class="mb-0">
                      <v-select
                        v-model="filters.enruRecipientType"
                        :options="recipientTypeOptions"
                        label="text"
                        :reduce="(option) => option.value"
                        placeholder="All types"
                        :disabled="isLoading"
                        class="searchable-select"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <div class="filter-actions">
                  <b-button variant="outline-secondary" size="sm" :disabled="isLoading" @click="resetFilters">
                    Reset
                  </b-button>
                  <b-button variant="primary" size="sm" :disabled="isLoading" @click="applyFilters">
                    Apply Filter
                  </b-button>
                </div>
              </div>

              <b-table
                responsive
                hover
                bordered
                small
                :busy="isLoading && activeMethod === 'GET'"
                :items="items"
                :fields="tableFields"
                show-empty
                empty-text="No event notification rule data."
              >
                <template #table-busy>
                  <div class="text-center py-4">
                    <b-spinner small class="mr-2"></b-spinner>
                    Loading...
                  </div>
                </template>

                <template #cell(event)="row">
                  <div class="cell-title">{{ getEventType(row.item) }}</div>
                  <small class="text-muted">ID: {{ row.item.enruMevtId }}</small>
                </template>

                <template #cell(asset)="row">
                  <div class="cell-title">{{ getAssetName(row.item) }}</div>
                  <small class="text-muted">ID: {{ row.item.enruMassId }}</small>
                </template>

                <template #cell(recipient)="row">
                  <div class="cell-title">{{ getRecipientName(row.item) }}</div>
                  <small class="text-muted">{{ getRecipientEmail(row.item) }}</small>
                </template>

                <template #cell(enruRecipientType)="row">
                  <b-badge class="type-badge">{{ row.item.enruRecipientType }}</b-badge>
                </template>

                <template #cell(actions)="row">
                  <div class="row-actions">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="isLoading"
                      @click="openEditRuleModal(row.item)"
                    >
                      Edit
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      :disabled="isLoading"
                      @click="confirmDelete(row.item)"
                    >
                      Delete
                    </b-button>
                  </div>
                </template>
              </b-table>

              <div class="d-flex justify-content-end mt-3">
                <b-pagination
                  v-model="pagination.page"
                  :total-rows="pagination.totalData"
                  :per-page="pagination.itemPerPage"
                  size="sm"
                  :disabled="isLoading"
                  @change="onPageChanged"
                />
              </div>
            </section>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-modal
      v-model="deleteModalVisible"
      title="Delete event notification rule"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="isLoading"
      :cancel-disabled="isLoading"
      @ok.prevent="deleteRule"
    >
      <p class="mb-2">
        Delete rule <strong>#{{ selectedRule.enruId }}</strong>?
      </p>
      <div class="text-muted">
        {{ getEventType(selectedRule) }} - {{ getRecipientName(selectedRule) }}
      </div>
    </b-modal>

    <b-modal
      v-model="addModalVisible"
      :title="isEditing ? 'Edit event notification rule' : 'Add event notification rule'"
      hide-footer
      lazy
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-form @submit.prevent="createRule">
        <b-form-group label="Event">
          <searchable-select
            v-model="form.event"
            :fetcher="searchEvents"
            placeholder="Select event"
            :disabled="isLoading"
          />
        </b-form-group>

        <b-form-group label="Asset">
          <searchable-select
            v-model="form.asset"
            :fetcher="searchAssets"
            placeholder="Select asset"
            :disabled="isLoading"
          />
        </b-form-group>

        <b-form-group label="Recipient">
          <searchable-select
            v-model="form.recipient"
            :fetcher="searchRecipients"
            placeholder="Select recipient"
            :disabled="isLoading"
          />
        </b-form-group>

        <b-form-group label="Recipient Type">
          <v-select
            v-model="form.enruRecipientType"
            :options="recipientTypeOptions"
            label="text"
            :reduce="(option) => option.value"
            placeholder="Select type"
            :disabled="isLoading"
            class="searchable-select"
          />
        </b-form-group>

        <div class="modal-actions">
          <b-button variant="outline-secondary" :disabled="isLoading" @click="closeAddRuleModal">
            Cancel
          </b-button>
          <b-button type="submit" variant="success" :disabled="isLoading || !canSubmit">
            <i v-if="isLoading && activeMethod === 'POST'" class="ri-loader-line mr-1"></i>
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </b-container>
</template>

<script>
import AssetSvc from '@/services/AssetSvc';
import EventSvc from '@/services/EventSvc';
import EventNotificationRuleSvc from '@/services/EventNotificationRuleSvc';
import NotificationRecipientSvc from '@/services/NotificationRecipientSvc';
import SearchableSelect from '@/components/master/SearchableSelect.vue';
import apiCrudMixin from '@/mixins/apiCrudMixin';

const SEARCH_RESULT_LIMIT = 50;

export default {
  name: 'EventNotificationRule',
  components: {
    SearchableSelect,
  },
  mixins: [apiCrudMixin],
  data() {
    return {
      addModalVisible: false,
      deleteModalVisible: false,
      editingId: null,
      form: {
        event: null,
        asset: null,
        recipient: null,
        enruRecipientType: 'to',
      },
      filters: {
        event: null,
        asset: null,
        recipient: null,
        enruRecipientType: null,
      },
      recipientTypeOptions: [
        { value: 'to', text: 'To' },
        { value: 'cc', text: 'Cc' },
        { value: 'bcc', text: 'Bcc' },
      ],
      selectedRule: {},
      tableFields: [
        { key: 'enruId', label: 'ID', thStyle: { width: '80px' } },
        { key: 'event', label: 'Event' },
        { key: 'asset', label: 'Asset' },
        { key: 'recipient', label: 'Recipient' },
        { key: 'enruRecipientType', label: 'Type', thStyle: { width: '90px' } },
        { key: 'enruUpdatedTime', label: 'Updated Time' },
        { key: 'actions', label: '', thStyle: { width: '100px' } },
      ],
    };
  },
  computed: {
    canSubmit() {
      return Boolean(
        this.form.event
        && this.form.asset
        && this.form.recipient
        && this.form.enruRecipientType,
      );
    },
    isEditing() {
      return this.editingId !== null;
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    applyFilters() {
      this.pagination.page = 1;
      this.fetchList();
    },
    buildSearchParams(field, query) {
      const params = { max: SEARCH_RESULT_LIMIT };
      if (query) {
        params[`finds[${field}]`] = query;
      }
      return params;
    },
    closeAddRuleModal() {
      this.addModalVisible = false;
    },
    confirmDelete(rule) {
      this.selectedRule = rule;
      this.deleteModalVisible = true;
    },
    async createRule() {
      const payload = {
        enruMevtId: this.form.event ? this.form.event.value : null,
        enruMassId: this.form.asset ? this.form.asset.value : null,
        enruNtrcId: this.form.recipient ? this.form.recipient.value : null,
        enruRecipientType: this.form.enruRecipientType,
      };
      const method = this.isEditing ? 'PUT' : 'POST';
      const requestFn = this.isEditing
        ? () => EventNotificationRuleSvc.update(this.editingId, payload)
        : () => EventNotificationRuleSvc.create(payload);

      await this.runRequest(method, requestFn, async () => {
        this.resetForm();
        this.addModalVisible = false;
        this.setMessage(this.isEditing ? 'Rule updated.' : 'Rule saved.');
        await this.fetchList(false);
      });
    },
    async deleteRule() {
      const id = this.selectedRule.enruId;
      await this.runRequest(
        'DELETE',
        () => EventNotificationRuleSvc.delete(id),
        async () => {
          this.deleteModalVisible = false;
          this.selectedRule = {};
          this.setMessage('Rule deleted.');
          await this.fetchList(false);
        },
      );
    },
    async fetchList(showLoading = true) {
      const params = {
        page: this.pagination.page,
        max: this.pagination.itemPerPage,
      };
      const finds = {
        enruMevtId: this.filters.event ? this.filters.event.value : null,
        enruMassId: this.filters.asset ? this.filters.asset.value : null,
        enruNtrcId: this.filters.recipient ? this.filters.recipient.value : null,
        enruRecipientType: this.filters.enruRecipientType,
      };
      Object.keys(finds).forEach((key) => {
        if (finds[key] !== null && finds[key] !== '') {
          params[`finds[${key}]`] = finds[key];
        }
      });

      await this.runRequest(
        'GET',
        () => EventNotificationRuleSvc.get(params),
        null,
        showLoading,
      );
    },
    getRows(response) {
      // Sebagian service mengembalikan axios response penuh ({ data: { data: [...] } }),
      // sebagian (mis. EventSvc) sudah unwrap jadi body ({ data: [...] }).
      const payload = response && response.data;
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    getAssetName(rule) {
      return rule && rule.enruMass && rule.enruMass.massName ? rule.enruMass.massName : '-';
    },
    getEventType(rule) {
      return rule && rule.enruMevt && rule.enruMevt.mevtType ? rule.enruMevt.mevtType : '-';
    },
    getRecipientEmail(rule) {
      return rule && rule.enruNtrc && rule.enruNtrc.ntrcEmail ? rule.enruNtrc.ntrcEmail : '';
    },
    getRecipientName(rule) {
      return rule && rule.enruNtrc && rule.enruNtrc.ntrcName ? rule.enruNtrc.ntrcName : '-';
    },
    openAddRuleModal() {
      this.resetForm();
      this.addModalVisible = true;
    },
    openEditRuleModal(rule) {
      this.editingId = rule.enruId;
      this.form = {
        event: {
          value: rule.enruMevtId,
          text: `${this.getEventType(rule)} (ID: ${rule.enruMevtId})`,
        },
        asset: {
          value: rule.enruMassId,
          text: `${this.getAssetName(rule)} (ID: ${rule.enruMassId})`,
        },
        recipient: {
          value: rule.enruNtrcId,
          text: `${this.getRecipientName(rule)} - ${this.getRecipientEmail(rule) || '-'} (ID: ${rule.enruNtrcId})`,
        },
        enruRecipientType: rule.enruRecipientType,
      };
      this.addModalVisible = true;
    },
    resetForm() {
      this.editingId = null;
      this.form = {
        event: null,
        asset: null,
        recipient: null,
        enruRecipientType: 'to',
      };
    },
    resetFilters() {
      this.filters = {
        event: null,
        asset: null,
        recipient: null,
        enruRecipientType: null,
      };
      this.pagination.page = 1;
      this.fetchList();
    },
    async searchAssets(query) {
      const response = await AssetSvc.getAssets({ params: this.buildSearchParams('massName', query) });
      return this.getRows(response).map((asset) => ({
        value: asset.massId,
        text: `${asset.massName || '-'} (ID: ${asset.massId})`,
      }));
    },
    async searchEvents(query) {
      const response = await EventSvc.get(this.buildSearchParams('mevtType', query));
      return this.getRows(response).map((event) => ({
        value: event.mevtId,
        text: `${event.mevtType || '-'} (ID: ${event.mevtId})`,
      }));
    },
    async searchRecipients(query) {
      const response = await NotificationRecipientSvc.get(this.buildSearchParams('ntrcName', query));
      return this.getRows(response).map((recipient) => ({
        value: recipient.ntrcId,
        text: `${recipient.ntrcName || '-'} - ${recipient.ntrcEmail || '-'} (ID: ${recipient.ntrcId})`,
      }));
    },
  },
};
</script>

<style src="@/views/master/_apiPanel.css" scoped></style>

<style scoped>
.filter-panel {
  border: 1px solid #eef1f5;
  border-radius: 8px;
  margin-bottom: 14px;
  padding: 12px;
  background: #fbfcfd;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cell-title {
  font-weight: 600;
}

.type-badge {
  background-color: #d1ecf1 !important;
  color: #0c5460 !important;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 4px 8px;
}

.searchable-select {
  width: 100%;
  background-color: #fff;
  border-radius: 4px;
}
</style>
